import type { Element, ElementContent, Properties } from 'hast';
import type { List, ListItem, Parents } from 'mdast';
import { State } from 'mdast-util-to-hast';
import { Note, RenderMode } from '../../feature/note/types';

/**
 * @param {ListItem} node
 * @return {Boolean}
 */
function listItemLoose(node: ListItem) {
  const {spread} = node

  return spread === null || spread === undefined
    ? node.children.length > 1
    : spread
}

/**
 * @param {Parents} node
 * @return {Boolean}
 */
function listLoose(node: Parents) {
  let loose = false
  if (node.type === 'list') {
    loose = node.spread || false
    const {children} = node
    let index = -1

    // eslint-disable-next-line no-plusplus
    while (!loose && ++index < children.length) {
      loose = listItemLoose(children[index])
    }
  }

  return loose
}

/**
 * Turn an mdast `listItem` node into hast.
 * @param {Note} note
 * @param {RenderMode} renderMode
 */
// eslint-disable-next-line import/prefer-default-export
export const listItem = (note: Note, renderMode: RenderMode) =>
  (state: State, node: ListItem, parent: List): Element => {

  const results = state.all(node)
  const loose = parent ? listLoose(parent) : listItemLoose(node)
  /** @type {Properties} */
  const properties: Properties = {}
  /** @type {Array<ElementContent>} */
  const children: ElementContent[] = []

  if (typeof node.checked === 'boolean') {
    const head = results[0]
    /** @type {Element} */
    let paragraph: Element;

    if (head && head.type === 'element' && head.tagName === 'p') {
      paragraph = head
    } else {
      paragraph = {type: 'element', tagName: 'p', properties: {}, children: []}
      results.unshift(paragraph)
    }

    if (paragraph.children.length > 0) {
      paragraph.children.unshift({type: 'text', value: ' '})
    }

    // == Notenote Task Start ==
    const task = note.tasks.find((t) => t.startPos === node.position?.start.offset);

    if (task && renderMode === 'View') {
      paragraph.children.unshift({
        type: 'element',
        tagName: 'input',
        properties: {
          type: 'checkbox',
          checked: node.checked,
          'data-note-id': note.id,
          'data-task-id': task.id,
          'data-task-completed': node.checked,
        },
        children: [],
      });
    } else {
      paragraph.children.unshift({
        type: 'element',
        tagName: 'input',
        properties: {
          type: 'checkbox',
          checked: node.checked,
          disabled: true,
        },
        children: [],
      });
    }

    // According to github-markdown-css, this class hides bullet.
    // See: <https://github.com/sindresorhus/github-markdown-css>.
    properties.className = node.checked ? ['task-list-item', 'complete'] : ['task-list-item'];
    // == Notenote Task End ==
  }

  let index = -1

  // eslint-disable-next-line no-plusplus
  while (++index < results.length) {
    const child = results[index]

    // Add eols before nodes, except if this is a loose, first paragraph.
    if (
      loose ||
      index !== 0 ||
      child.type !== 'element' ||
      child.tagName !== 'p'
    ) {
      children.push({type: 'text', value: '\n'})
    }

    if (child.type === 'element' && child.tagName === 'p' && !loose) {
      children.push(...child.children)
    } else {
      children.push(child)
    }
  }

  const tail = results[results.length - 1]

  // Add a final eol.
  if (tail && (loose || tail.type !== 'element' || tail.tagName !== 'p')) {
    children.push({type: 'text', value: '\n'})
  }

  /** @type {Element} */
  const result: Element = {type: 'element', tagName: 'li', properties, children}
  state.patch(node, result)
  return state.applyData(node, result)
}
